import { FC, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@screentone/addon-auth-wrapper';
import { Button, Dialog, FormHelperText, FormLabel, IconPlusCircle, Input, Loader, Wrapper } from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';

import { NoSearchResults } from 'components/no-search-results/NoSearchResults';
import { useCreateMobileIssueMutation } from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import env from 'utils/env';
import styles from './MobileIssueManagement.module.scss';

function deriveIssuePrefix(mobileIssues: string[], publicationKey: string): string {
  const first = mobileIssues.find((id) => id.split('_').length >= 2);
  if (first) {
    const parts = first.split('_');
    return `${parts[0]}_${parts[1]}_`;
  }
  return `${env.UCS_COLLECTION_PREFIX}_${publicationKey.toUpperCase()}_`;
}

const MobileIssueManagement: FC = () => {
  const { data } = usePublicationSettings();
  const { currentProperty } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutateAsync: createMobileIssueMutateAsync, isLoading: isCreating } = useCreateMobileIssueMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [suffix, setSuffix] = useState('');
  const [error, setError] = useState('');

  const mobileIssues = data?.publicationSetting.mobileIssues ?? [];
  const prefix = deriveIssuePrefix(mobileIssues, currentProperty ?? '');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // Prevent editing into the fixed prefix
    if (!newValue.startsWith(prefix)) return;
    const raw = newValue.slice(prefix.length).toUpperCase().replace(/[^A-Z0-9_-]/g, '');
    setSuffix(raw);
    setError('');
  };

  const handleOpen = () => {
    setSuffix('');
    setError('');
    setDialogOpen(true);
  };

  const handleDismiss = () => {
    setDialogOpen(false);
  };

  const handleConfirm = async () => {
    if (!suffix.trim()) {
      setError('Issue key suffix is required.');
      return;
    }
    const fullId = `${prefix}${suffix}`;
    if (mobileIssues.includes(fullId)) {
      setError(`An issue with ID "${fullId}" already exists.`);
      return;
    }
    try {
      const result = await createMobileIssueMutateAsync({
        mobileIssueDTOCreateInput: { publicationKey: currentProperty ?? '', issueKey: suffix },
      });
      const newAllessehId = result.createMobileIssue.metadata.allessehId;
      setDialogOpen(false);
      void queryClient.invalidateQueries(['PublicationSettings']);
      navigate(newAllessehId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create mobile issue.');
    }
  };

  return (
    <Wrapper>
      <Button
        icon={IconPlusCircle}
        onClick={handleOpen}
        tertiary
        margin={{ bottom: 'sm' }}
        data-testid="mobile-issue-add-button"
      >
        Add new issue
      </Button>

      <div className={styles.mobileIssuesList} data-testid="mobile-issues-list">
        {mobileIssues.length > 0 ? (
          mobileIssues.map((mobileIssue) => (
            <Button key={mobileIssue} componentEl={Link} to={mobileIssue} secondary data-testid="mobile-issue-item">
              {mobileIssue}
            </Button>
          ))
        ) : (
          <NoSearchResults title="Mobile Issues" bodyText="There are no mobile issues for this publication." />
        )}
      </div>

      <Dialog status={dialogOpen ? 'open' : 'closed'} onDismiss={handleDismiss}>
        <Dialog.Title id="createMobileIssueTitle">Add New Mobile Issue</Dialog.Title>
        <Dialog.Content id="createMobileIssueContent">
          <FormLabel labelPosition="top" label="Issue ID" margin={{ bottom: 'xs' }} htmlFor="mobile-issue-suffix-input" />
          <Input
            id="mobile-issue-suffix-input"
            value={`${prefix}${suffix}`}
            onChange={handleInputChange}
            placeholder={`${prefix}WHATS_NEWS_AM`}
            data-testid="mobile-issue-suffix-input"
          />
          {error && <FormHelperText error margin={{ top: 'xs' }}>{error}</FormHelperText>}
        </Dialog.Content>
        <Dialog.Actions>
          <Button secondary onClick={handleDismiss}>
            Cancel
          </Button>
          <Button primary onClick={handleConfirm} disabled={!suffix.trim() || isCreating} data-testid="mobile-issue-confirm-create">
            {isCreating && <Loader size="md" margin={{ right: 'md' }} />}
            Create
          </Button>
        </Dialog.Actions>
      </Dialog>
    </Wrapper>
  );
};

export default MobileIssueManagement;
